import { RefrigerantCode } from '../types';

/**
 * Defines the possible status values for superheat and subcooling.
 */
export type HvacStatus = "High" | "Low" | "Normal";

export interface RefrigerantStatus {
  status: "Critical (Very Low)" | "Warning (Low)" | "Optimal" | "Warning (High)" | "Critical (Very High)";
  emoji: "🚨" | "❗" | "✅";
  superheatRangeMinC: number;
  superheatRangeMaxC: number;
  subcoolingRangeMinC: number;
  subcoolingRangeMaxC: number;
}

export interface RefrigerantRangeData {
  name: string;
  description: string;
  targetSuperheatMinC: number;
  targetSuperheatMaxC: number;
  targetSubcoolingMinC: number;
  targetSubcoolingMaxC: number;
  ranges: RefrigerantStatus[];
}

export const hvacRefrigerantRanges: { [key in RefrigerantCode]?: RefrigerantRangeData } = {
  [RefrigerantCode.R22]: {
    name: "R22",
    description: "Common in older residential/commercial AC",
    targetSuperheatMinC: 5.6,
    targetSuperheatMaxC: 11.1,
    targetSubcoolingMinC: 5.6,
    targetSubcoolingMaxC: 8.3,
    ranges: [
      {
        status: "Critical (Very Low)",
        emoji: "🚨",
        superheatRangeMinC: 0,
        superheatRangeMaxC: 2.2,
        subcoolingRangeMinC: 0,
        subcoolingRangeMaxC: 2.2,
      },
      {
        status: "Warning (Low)",
        emoji: "❗",
        superheatRangeMinC: 2.8,
        superheatRangeMaxC: 5.0,
        subcoolingRangeMinC: 2.8,
        subcoolingRangeMaxC: 5.0,
      },
      {
        status: "Optimal",
        emoji: "✅",
        superheatRangeMinC: 5.6,
        superheatRangeMaxC: 11.1,
        subcoolingRangeMinC: 5.6,
        subcoolingRangeMaxC: 8.3,
      },
      {
        status: "Warning (High)",
        emoji: "❗",
        superheatRangeMinC: 11.7,
        superheatRangeMaxC: 16.1,
        subcoolingRangeMinC: 8.9,
        subcoolingRangeMaxC: 13.3,
      },
      {
        status: "Critical (Very High)",
        emoji: "🚨",
        superheatRangeMinC: 16.7,
        superheatRangeMaxC: Infinity,
        subcoolingRangeMinC: 13.9,
        subcoolingRangeMaxC: Infinity,
      },
    ],
  },
  [RefrigerantCode.R134a]: {
    name: "R134a",
    description: "Common in medium-temp refrigeration & automotive AC",
    targetSuperheatMinC: 5.6,
    targetSuperheatMaxC: 11.1,
    targetSubcoolingMinC: 4.4,
    targetSubcoolingMaxC: 6.7,
    ranges: [
      {
        status: "Critical (Very Low)",
        emoji: "🚨",
        superheatRangeMinC: 0,
        superheatRangeMaxC: 2.2,
        subcoolingRangeMinC: 0,
        subcoolingRangeMaxC: 1.7,
      },
      {
        status: "Warning (Low)",
        emoji: "❗",
        superheatRangeMinC: 2.8,
        superheatRangeMaxC: 5.0,
        subcoolingRangeMinC: 2.2,
        subcoolingRangeMaxC: 3.9,
      },
      {
        status: "Optimal",
        emoji: "✅",
        superheatRangeMinC: 5.6,
        superheatRangeMaxC: 11.1,
        subcoolingRangeMinC: 4.4,
        subcoolingRangeMaxC: 6.7,
      },
      {
        status: "Warning (High)",
        emoji: "❗",
        superheatRangeMinC: 11.7,
        superheatRangeMaxC: 16.1,
        subcoolingRangeMinC: 7.2,
        subcoolingRangeMaxC: 10.6,
      },
      {
        status: "Critical (Very High)",
        emoji: "🚨",
        superheatRangeMinC: 16.7,
        superheatRangeMaxC: Infinity,
        subcoolingRangeMinC: 11.1,
        subcoolingRangeMaxC: Infinity,
      },
    ],
  },
  [RefrigerantCode.R1234yf]: {
    name: "R1234yf",
    description: "Common in newer automotive AC",
    targetSuperheatMinC: 2.8,
    targetSuperheatMaxC: 11.1,
    targetSubcoolingMinC: 2.8,
    targetSubcoolingMaxC: 8.3,
    ranges: [
      {
        status: "Critical (Very Low)",
        emoji: "🚨",
        superheatRangeMinC: 0,
        superheatRangeMaxC: 1.1,
        subcoolingRangeMinC: 0,
        subcoolingRangeMaxC: 1.1,
      },
      {
        status: "Warning (Low)",
        emoji: "❗",
        superheatRangeMinC: 1.7,
        superheatRangeMaxC: 2.2,
        subcoolingRangeMinC: 1.7,
        subcoolingRangeMaxC: 2.2,
      },
      {
        status: "Optimal",
        emoji: "✅",
        superheatRangeMinC: 2.8,
        superheatRangeMaxC: 11.1,
        subcoolingRangeMinC: 2.8,
        subcoolingRangeMaxC: 8.3,
      },
      {
        status: "Warning (High)",
        emoji: "❗",
        superheatRangeMinC: 11.7,
        superheatRangeMaxC: 16.1,
        subcoolingRangeMinC: 8.9,
        subcoolingRangeMaxC: 13.3,
      },
      {
        status: "Critical (Very High)",
        emoji: "🚨",
        superheatRangeMinC: 16.7,
        superheatRangeMaxC: Infinity,
        subcoolingRangeMinC: 13.9,
        subcoolingRangeMaxC: Infinity,
      },
    ],
  },
  [RefrigerantCode.R410A]: {
    name: "R410A",
    description: "Common in modern residential/commercial AC",
    targetSuperheatMinC: 4.4,
    targetSuperheatMaxC: 6.7,
    targetSubcoolingMinC: 4.4,
    targetSubcoolingMaxC: 6.7,
    ranges: [
      {
        status: "Critical (Very Low)",
        emoji: "🚨",
        superheatRangeMinC: 0,
        superheatRangeMaxC: 1.7,
        subcoolingRangeMinC: 0,
        subcoolingRangeMaxC: 1.7,
      },
      {
        status: "Warning (Low)",
        emoji: "❗",
        superheatRangeMinC: 2.2,
        superheatRangeMaxC: 3.9,
        subcoolingRangeMinC: 2.2,
        subcoolingRangeMaxC: 3.9,
      },
      {
        status: "Optimal",
        emoji: "✅",
        superheatRangeMinC: 4.4,
        superheatRangeMaxC: 6.7,
        subcoolingRangeMinC: 4.4,
        subcoolingRangeMaxC: 6.7,
      },
      {
        status: "Warning (High)",
        emoji: "❗",
        superheatRangeMinC: 7.2,
        superheatRangeMaxC: 10.6,
        subcoolingRangeMinC: 7.2,
        subcoolingRangeMaxC: 10.6,
      },
      {
        status: "Critical (Very High)",
        emoji: "🚨",
        superheatRangeMinC: 11.1,
        superheatRangeMaxC: Infinity,
        subcoolingRangeMinC: 11.1,
        subcoolingRangeMaxC: Infinity,
      },
    ],
  },
  [RefrigerantCode.R404A]: {
    name: "R404A",
    description: "Common in low/medium-temp commercial refrigeration",
    targetSuperheatMinC: 3.3,
    targetSuperheatMaxC: 5.6,
    targetSubcoolingMinC: 3.3,
    targetSubcoolingMaxC: 5.6,
    ranges: [
      {
        status: "Critical (Very Low)",
        emoji: "🚨",
        superheatRangeMinC: 0,
        superheatRangeMaxC: 1.1,
        subcoolingRangeMinC: 0,
        subcoolingRangeMaxC: 1.1,
      },
      {
        status: "Warning (Low)",
        emoji: "❗",
        superheatRangeMinC: 1.7,
        superheatRangeMaxC: 2.8,
        subcoolingRangeMinC: 1.7,
        subcoolingRangeMaxC: 2.8,
      },
      {
        status: "Optimal",
        emoji: "✅",
        superheatRangeMinC: 3.3,
        superheatRangeMaxC: 5.6,
        subcoolingRangeMinC: 3.3,
        subcoolingRangeMaxC: 5.6,
      },
      {
        status: "Warning (High)",
        emoji: "❗",
        superheatRangeMinC: 6.1,
        superheatRangeMaxC: 9.4,
        subcoolingRangeMinC: 6.1,
        subcoolingRangeMaxC: 9.4,
      },
      {
        status: "Critical (Very High)",
        emoji: "🚨",
        superheatRangeMinC: 10.0,
        superheatRangeMaxC: Infinity,
        subcoolingRangeMinC: 10.0,
        subcoolingRangeMaxC: Infinity,
      },
    ],
  },
  [RefrigerantCode.R407C]: {
    name: "R407C",
    description: "Common replacement for R22 in residential/commercial AC",
    targetSuperheatMinC: 5.6,
    targetSuperheatMaxC: 11.1,
    targetSubcoolingMinC: 4.4,
    targetSubcoolingMaxC: 8.3,
    ranges: [
      {
        status: "Critical (Very Low)",
        emoji: "🚨",
        superheatRangeMinC: 0,
        superheatRangeMaxC: 2.2,
        subcoolingRangeMinC: 0,
        subcoolingRangeMaxC: 1.7,
      },
      {
        status: "Warning (Low)",
        emoji: "❗",
        superheatRangeMinC: 2.8,
        superheatRangeMaxC: 5.0,
        subcoolingRangeMinC: 2.2,
        subcoolingRangeMaxC: 3.9,
      },
      {
        status: "Optimal",
        emoji: "✅",
        superheatRangeMinC: 5.6,
        superheatRangeMaxC: 11.1,
        subcoolingRangeMinC: 4.4,
        subcoolingRangeMaxC: 8.3,
      },
      {
        status: "Warning (High)",
        emoji: "❗",
        superheatRangeMinC: 11.7,
        superheatRangeMaxC: 16.1,
        subcoolingRangeMinC: 8.9,
        subcoolingRangeMaxC: 13.3,
      },
      {
        status: "Critical (Very High)",
        emoji: "🚨",
        superheatRangeMinC: 16.7,
        superheatRangeMaxC: Infinity,
        subcoolingRangeMinC: 13.9,
        subcoolingRangeMaxC: Infinity,
      },
    ],
  },
  [RefrigerantCode.R32]: {
    name: "R32",
    description: "Common in modern residential AC systems",
    targetSuperheatMinC: 2.8,
    targetSuperheatMaxC: 8.3,
    targetSubcoolingMinC: 2.8,
    targetSubcoolingMaxC: 5.6,
    ranges: [
      {
        status: "Critical (Very Low)",
        emoji: "🚨",
        superheatRangeMinC: 0,
        superheatRangeMaxC: 1.1,
        subcoolingRangeMinC: 0,
        subcoolingRangeMaxC: 1.1,
      },
      {
        status: "Warning (Low)",
        emoji: "❗",
        superheatRangeMinC: 1.7,
        superheatRangeMaxC: 2.2,
        subcoolingRangeMinC: 1.7,
        subcoolingRangeMaxC: 2.2,
      },
      {
        status: "Optimal",
        emoji: "✅",
        superheatRangeMinC: 2.8,
        superheatRangeMaxC: 8.3,
        subcoolingRangeMinC: 2.8,
        subcoolingRangeMaxC: 5.6,
      },
      {
        status: "Warning (High)",
        emoji: "❗",
        superheatRangeMinC: 8.9,
        superheatRangeMaxC: 13.3,
        subcoolingRangeMinC: 6.1,
        subcoolingRangeMaxC: 10.6,
      },
      {
        status: "Critical (Very High)",
        emoji: "🚨",
        superheatRangeMinC: 13.9,
        superheatRangeMaxC: Infinity,
        subcoolingRangeMinC: 11.1,
        subcoolingRangeMaxC: Infinity,
      },
    ],
  },
};

// Helper function to get the status of a superheat/subcooling value for a specific refrigerant
export function getRefrigerantStatus(
  refrigerant: RefrigerantCode, 
  superheatC: number, 
  subcoolingC: number
): {
  superheatStatus: RefrigerantStatus | null;
  subcoolingStatus: RefrigerantStatus | null;
  refrigerantData: RefrigerantRangeData | null;
} {
  const data = hvacRefrigerantRanges[refrigerant];
  
  if (!data) {
    return {
      superheatStatus: null,
      subcoolingStatus: null,
      refrigerantData: null
    };
  }

  let superheatStatus: RefrigerantStatus | null = null;
  let subcoolingStatus: RefrigerantStatus | null = null;

  // Find superheat status
  for (const range of data.ranges) {
    if (superheatC >= range.superheatRangeMinC && superheatC <= range.superheatRangeMaxC) {
      superheatStatus = range;
      break;
    }
  }

  // Find subcooling status
  for (const range of data.ranges) {
    if (subcoolingC >= range.subcoolingRangeMinC && subcoolingC <= range.subcoolingRangeMaxC) {
      subcoolingStatus = range;
      break;
    }
  }

  return {
    superheatStatus,
    subcoolingStatus,
    refrigerantData: data
  };
}

/**
 * Provides a potential diagnosis for an HVAC system based on its superheat and subcooling status.
 *
 * The logic is based on the following standard HVAC troubleshooting chart:
 *
 * | Superheat | Subcooling | Potential Problem                                           |
 * |-----------|------------|-------------------------------------------------------------|
 * | High      | Low        | Undercharged with refrigerant.                              |
 * | Low       | High       | Overcharged with refrigerant or restricted condenser airflow. |
 * | High      | High       | Restriction in the liquid line (e.g., clogged filter drier).  |
 * | Low       | Low        | Inefficient compressor or a faulty/overfeeding metering device. |
 * | Normal    | High       | Condenser airflow issue or a slight overcharge.             |
 * | Low       | Normal     | Evaporator airflow issue or a faulty metering device.       |
 * | High      | Normal     | Metering device is underfeeding or slight undercharge.      |
 * | Normal    | Low        | Inefficient compressor or a slight undercharge.             |
 * | Normal    | Normal     | System appears to be operating normally.                    |
 *
 * @param superheatStatus The status of the superheat reading ('High', 'Low', or 'Normal').
 * @param subcoolingStatus The status of the subcooling reading ('High', 'Low', or 'Normal').
 * @returns A string describing the potential problem or status of the system.
 */
export function getHvacTroubleshootingInfo(
  superheatStatus: HvacStatus,
  subcoolingStatus: HvacStatus
): string {
  if (superheatStatus === "High" && subcoolingStatus === "Low") {
    return "Undercharged with refrigerant.";
  }
  if (superheatStatus === "Low" && subcoolingStatus === "High") {
    return "Overcharged with refrigerant or restricted condenser airflow.";
  }
  if (superheatStatus === "High" && subcoolingStatus === "High") {
    return "Restriction in the liquid line (e.g., clogged filter drier).";
  }
  if (superheatStatus === "Low" && subcoolingStatus === "Low") {
    return "Inefficient compressor or a faulty/overfeeding metering device.";
  }
  if (superheatStatus === "Normal" && subcoolingStatus === "High") {
    return "Condenser airflow issue (e.g., dirty coil, failing fan) or a slight overcharge.";
  }
  if (superheatStatus === "Low" && subcoolingStatus === "Normal") {
    return "Evaporator airflow issue (e.g., dirty filter/coil) or a faulty metering device.";
  }
  if (superheatStatus === "High" && subcoolingStatus === "Normal") {
    return "Metering device is underfeeding or the system has a slight undercharge.";
  }
  if (superheatStatus === "Normal" && subcoolingStatus === "Low") {
    return "Inefficient compressor or a slight undercharge.";
  }
  if (superheatStatus === "Normal" && subcoolingStatus === "Normal") {
    return "System appears to be operating normally.";
  }

  // This fallback should not be reached if valid inputs are used,
  // as all 9 combinations of 'High', 'Low', and 'Normal' are handled.
  return "Unable to determine status with the given combination.";
}

// Helper function to determine HVAC status based on refrigerant-specific ranges
export function getHvacStatus(
  refrigerant: RefrigerantCode,
  superheatC: number,
  subcoolingC: number
): {
  superheatStatus: HvacStatus;
  subcoolingStatus: HvacStatus;
} {
  const data = hvacRefrigerantRanges[refrigerant];
  
  if (!data) {
    // Fallback to generic ranges
    const superheatStatus: HvacStatus = 
      superheatC < 8 ? "Low" : superheatC > 15 ? "High" : "Normal";
    const subcoolingStatus: HvacStatus = 
      subcoolingC < 5 ? "Low" : subcoolingC > 12 ? "High" : "Normal";
    
    return { superheatStatus, subcoolingStatus };
  }

  // Use refrigerant-specific optimal ranges
  const superheatStatus: HvacStatus = 
    superheatC < data.targetSuperheatMinC ? "Low" : 
    superheatC > data.targetSuperheatMaxC ? "High" : "Normal";
    
  const subcoolingStatus: HvacStatus = 
    subcoolingC < data.targetSubcoolingMinC ? "Low" : 
    subcoolingC > data.targetSubcoolingMaxC ? "High" : "Normal";

  return { superheatStatus, subcoolingStatus };
} 